// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## scalePlayAndSpell.js
//
// Play scales on the piano, then spell them in letters.
import InteractivePrototype from './interactivePrototype.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';

class ScalePlayAndSpell extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeed =
      this.setTargetSpeed([
        'major_scales_piano',
        'major_scales_staff',
        'natural_minor_piano',
        'natural_minor',
        'harmonic_minor',
        'harmonic_minor_piano',
        'melodic_minor',
        'melodic_minor_piano',
      ]) / 8;
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 'all',
        noWrap: true,
      },
      piano: {
        ariaLive: 'polite',
      },
      checkMark: { rightAnswerLength: 1.2 },
      letters: { letterButtons: true, accidentalButtons: true, checkButton: 'Next' },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // register some options locally:
    this.showNumbers = !!options.showNumbers;
    this.checkAtEnd = !!options.checkAtEnd;

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    // In non drill mode, specify questions as:
    //  keynote/mode/asc or dsc
    //  modes: Major, minor, harmonicMinor, melodicMinor
    if (this.optionsTrue('drill')) {
      this.randomQuestionGenerator();
    } else {
      this.processManualQuestions();
    }

    this.prepareQuestions();
  }

  // read in this.options.questions:
  processManualQuestions() {
    this.scales = [];
    this.options.questions.forEach((question) => {
      const parts = question.split('/');
      const note = new Notation.Note(parts[0]);
      const mode = parts[1];
      console.log(`Mode: ${mode}`);
      const direction = typeof parts[2] !== 'undefined' && parts[2] === 'dsc' ? -1 : 1;
      const scale = new Notation.Scale(note, mode, direction);
      this.scales.push(scale);
    }, this);
  }

  prepareQuestions() {
    this.questions = [];
    this.scales.forEach((scale) => {
      const scaleText = scale.toAriaString({}, true);
      const text = `${utils.aOrAn(scaleText)} ${scaleText}`;
      const pianoSkill = this.calculatePianoSkill(scale.mode);
      const staffSkill = this.calculateStaffSkill(scale.mode);
      const questionObject = {
        scale,
        text: [text, this.questions.length + 1],
        pianoSkills: [pianoSkill],
        staffSkills: [staffSkill],
        skills: [staffSkill],
        direction: scale.direction,
      };
      this.questions.push(questionObject);
    }, this);
    // set up global variables:
    if (this.optionsTrue('questionPrompt')) {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt = 'Play {question} scale.';
    }
    this.maxQuestions = this.questions.length;
    this.questionPrompt += `<br /><small>#{question} of ${this.maxQuestions}</small>`;
    this.questionNumber = 0;
    this.scaleDegree = 0;
  }

  calculatePianoSkill(mode) {
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    return this.skillCalculator.getPianoScaleSkill(mode);
  }

  calculateStaffSkill(mode) {
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    return this.skillCalculator.getStaffScaleSkill(mode);
  }

  // or generate random questions:
  randomQuestionGenerator() {
    const generator = new QuestionGenerator();
    this.scales = generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions,
      this.options
    );
    console.log(this.scales);
    // randomize direction:
    this.scales.forEach((scale) => {
      if (Math.random() > 0.5) {
        scale.setDirection(-1);
      }
    });
  }

  addListeners() {
    this.piano.addNoteOnListener(this.checkPianoAnswer, this);
    this.letterPanel.addLetterCallback(this.updateNoteName, this);
    this.letterPanel.addAccidentalCallback(this.updateNoteName, this);
    this.letterPanel.addCheckCallback(this.checkLetterAnswer, this);
  }

  // ## Display new question / increment:

  newQuestion() {
    this.switchMode(false);
    this.question = this.questions[this.questionNumber];
    const tonic = this.question.scale.notes[0];
    console.log(`Tonic: ${tonic}`);
    const lowest = Notation.Interval.getNoteAtGenericInterval(tonic, -3);
    console.log(`Lowest: ${lowest}`);
    const highest = Notation.Interval.getNoteAtGenericInterval(tonic, 10);
    console.log(`Highest: ${highest}`);
    this.piano.setBoundaries(lowest, highest);

    this.scaleDegree = this.question.direction === 1 ? 0 : 7;
    super.newQuestion();
  }

  switchMode(spellingMode) {
    this.spellingMode = spellingMode;
    // Reset scale degree to 0 or 7:
    this.piano.setInteraction(!spellingMode);

    if (spellingMode) {
      // Make letter panel visible
      this.layout.letters.visible = true;

      // Reset scale degree:
      this.scaleDegree = this.question.direction === 1 ? 0 : 7;

      // Highlight first note of piano:
      this.displaySpellingQuestion();
    } else {
      // Hide letter panel
      this.layout.letters.visible = false;
    }
    this.resize();
  }

  displaySpellingQuestion() {
    const nextMidi = this.question.scale.notes[this.scaleDegree].getMidiNumber();
    this.piano.colorNote(nextMidi);
    this.piano.labelNote(nextMidi, '_');

    this.player.noteOn(nextMidi);
    // Update instructions labeling:
    let questionPrompt = 'Now spell {question} scale in letters.';
    questionPrompt += `<br /><small>#{question} of ${this.maxQuestions}</small>`;

    let questionPart = 0;
    let questionText = this.questions[this.questionNumber].text;
    if (!(questionText instanceof Array)) questionText = [questionText];
    while (questionPrompt.search('{question}') !== -1) {
      questionPrompt = questionPrompt.replace('{question}', questionText[questionPart]);
      questionPart++;
    }
    this.instructionsDiv.innerHTML = questionPrompt;
  }

  displayInstructions() {
    let questionPrompt = this.questionPrompt;
    let questionPart = 0;
    let questionText = this.questions[this.questionNumber].text;
    if (!(questionText instanceof Array)) questionText = [questionText];
    while (questionPrompt.search('{question}') !== -1) {
      questionPrompt = questionPrompt.replace('{question}', questionText[questionPart]);
      questionPart++;
    }
    this.instructionsDiv.innerHTML = questionPrompt;
  }

  // ## Checking & Processing Routines

  checkLetterAnswer() {
    if (!this.spellingMode) return;
    if (this.rightAnswerDelay) return;
    const letterName = this.letterPanel.getNoteName();
    // If there's no letterName, tell them they have to choose a letter:
    if (letterName === false) {
      this.checkMark.warn('Choose the letter name of the highlighted note first.');
      return;
    }
    this.checkMark.off();
    const goalNote = this.question.scale.notes[this.scaleDegree];
    const answerNote = new Notation.Note(letterName);
    const midiNumber = goalNote.getMidiNumber();

    if (goalNote.equalsIgnoreOctave(answerNote)) this.rightLetterName(midiNumber);
    else {
      this.wrongAnswer();
      // briefly turn it red:
      this.piano.colorNote(midiNumber, 'red', true);
      // then recolor it green/highlighted color:
      window.setTimeout(() => {
        this.piano.colorNote(midiNumber, undefined, true);
      }, 500);
    }
  }

  updateNoteName() {
    if (!this.spellingMode) return;
    const goalMidi = this.question.scale.notes[this.scaleDegree].getMidiNumber();
    const noteName = this.letterPanel.getPartialNoteName();
    this.piano.labelNote(goalMidi, noteName);
  }

  rightLetterName(midiNumber) {
    this.scaleDegree += this.question.direction;
    if (this.scaleDegree > 7 || this.scaleDegree < 0) {
      // If last note of scale -- prepare to make a new question:
      window.setTimeout(() => {
        this.piano.clearNoteColoring(midiNumber);
        this.player.noteOff(midiNumber);
        this.piano.clearNoteLabels();
      }, 1100);
      this.rightAnswer(1200);
    } else {
      // If middle of scale, record the right answer w/ tracker:
      if (this.settings.progressTracker) {
        this.progressTracker.rightAnswer(this.questions[this.questionNumber].staffSkills, false);
      }
      // And highlight the next note:
      this.displaySpellingQuestion();
    }
    this.letterPanel.clearNoteName();

    this.player.noteOff(midiNumber);
    this.piano.clearNoteColoring(midiNumber);
  }

  checkPianoAnswer(midiNumber) {
    if (this.spellingMode) return;
    if (this.rightAnswerDelay) return;
    this.checkMark.off();
    const goalNote = this.question.scale.notes[this.scaleDegree];
    const goalMidi = goalNote.getMidiNumber();
    if (goalMidi === midiNumber) this.rightScaleDegree(midiNumber);
    else {
      if (goalMidi % 12 === midiNumber % 12) {
        if (goalMidi > midiNumber) this.checkMark.warn('Start an octave higher.');
        else this.checkMark.warn('Start an octave lower.');
        return;
      }
      this.piano.colorNote(midiNumber, 'red', true);
      window.setTimeout(() => {
        this.piano.clearNoteColoring(midiNumber, undefined, true);
      }, 500);
      this.wrongAnswer();
    }
  }

  rightScaleDegree(midiNumber) {
    const rightDegree = this.scaleDegree + 1;
    this.scaleDegree += this.question.direction;
    if (this.scaleDegree > 7 || this.scaleDegree < 0) {
      this.spellingMode = true; // immediately! see github issue #11
      // If end of scale:
      window.setTimeout(() => {
        this.piano.clearNoteColoring(midiNumber);
        this.player.noteOff(midiNumber);
        const spelling = true;
        this.switchMode(spelling);
      }, 1100);
      //        this.rightAnswer(1200);
    }
    if (this.settings.progressTracker) {
      this.progressTracker.rightAnswer(this.questions[this.questionNumber].pianoSkills, false);
    }
    // Play it back:
    if (typeof this.priorMidi !== 'undefined') {
      this.player.noteOff(this.priorMidi);
      this.piano.clearNoteColoring(this.priorMidi);
    }
    this.player.noteOn(midiNumber);
    this.priorMidi = midiNumber;
    // Color the piano:
    this.piano.colorNote(midiNumber);
    if (!this.optionsTrue('hideLabels')) {
      this.piano.labelNote(midiNumber, rightDegree);
    }
  }

  debugRightAnswer() {
    if (this.spellingMode) this.rightLetterName(60);
    else this.rightScaleDegree(60);
  }

  // Game over:
  gameOver() {
    this.lettersDiv.style.display = 'none';
    this.piano.removeListeners();
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    super.gameOver();
  }

  // helper function to check options variable
  optionsTrue(property) {
    return typeof this.options[property] !== 'undefined' && this.options[property] !== false;
  }

  playAnswer() {}
}

export default ScalePlayAndSpell;
